/**
 * iOS 26+ Detection Module
 * Detects iOS 26 and higher versions reliably despite Apple's privacy changes
 */

/**
 * Check if device is iOS (iPhone, iPad, iPod)
 * @returns {boolean}
 */
export function isDeviceIOS() {
  const userAgent = window.navigator.userAgent;
  return (
    /iPhone|iPad|iPod/.test(userAgent) ||
    // iPad detection for iOS 13+ (reports as Macintosh)
    (/Macintosh/.test(userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1)
  );
}

/**
 * Check if browser is iOS Safari
 * @returns {boolean}
 */
function isBrowserIOSSafari() {
  const userAgent = window.navigator.userAgent;
  return (
    isDeviceIOS() &&
    /Safari/.test(userAgent) &&
    !/CriOS/.test(userAgent) && // Not Chrome
    !/FxiOS/.test(userAgent) && // Not Firefox
    !/FBAN|FBAV/.test(userAgent) && // Not Facebook
    !/LinkedInApp/.test(userAgent) && // Not LinkedIn
    !window.document.referrer.match('//l.instagram.com/') && // Not Instagram
    !window.document.referrer.match('//t.co/') // Not Twitter
  );
}

/**
 * Check if iOS version is 26 or higher
 * NOTE: iOS 26 Safari reports fixed OS version (18_6) for privacy
 * We use Safari version detection instead of OS version for reliability
 * @returns {boolean}
 */
function isIOSVersion26OrHigher() {
  if (!isDeviceIOS()) return false;

  const userAgent = window.navigator.userAgent;

  // CRITICAL: iOS 26 Safari reports fixed iOS version (18_6) for privacy
  // Use Safari version detection instead
  const safariVersionMatch = userAgent.match(/Version\/(\d+)/);
  if (safariVersionMatch) {
    const safariVersion = parseInt(safariVersionMatch[1], 10);
    // Safari 26+ indicates iOS 26+ (even if OS version shows 18_6)
    return safariVersion >= 26;
  }

  // Fallback: Check for actual iOS version (works for Chrome/Firefox on iOS)
  const osVersionMatch = userAgent.match(/OS (\d+)_/);
  if (osVersionMatch) {
    const majorVersion = parseInt(osVersionMatch[1], 10);
    return majorVersion >= 26;
  }

  return false;
}

/**
 * Check if browser is iOS Safari version 26 or higher
 * @returns {boolean}
 */
export function isBrowserIOSSafari26() {
  if (!isBrowserIOSSafari()) return false;

  const userAgent = window.navigator.userAgent;

  // Use Safari version detection for iOS 26+ Safari
  const safariVersionMatch = userAgent.match(/Version\/(\d+)/);
  if (safariVersionMatch) {
    const safariVersion = parseInt(safariVersionMatch[1], 10);
    return safariVersion >= 26;
  }

  return false;
}
